/**
 * Preview: quick settings beside the real, working widget.
 *
 * The widget in the right-hand pane is the shipped `widget.js` talking to the
 * live backend, not a mock — so a message typed here is a genuine conversation
 * that appears in the Chat Inbox. That is deliberate: the fastest way to know
 * a bot is configured correctly is to talk to it.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Spinner,
} from '../components/ui';
import { WidgetLivePreview } from '../components/WidgetPreview';
import { useBotSettings } from '../hooks/useBotSettings';
import { useBots } from '../hooks/useBots';
import { botLabel } from '../lib/format';

interface ChecklistItem {
  label: string;
  done: boolean;
  to: string;
  hint: string;
}

export function Preview(): JSX.Element {
  const { selected } = useBots();
  const slug = selected?.slug ?? null;
  const state = useBotSettings(slug);

  const [trainedSources, setTrainedSources] = useState<number | null>(null);
  const [conversations, setConversations] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const loadProgress = useCallback(async () => {
    if (!slug) return;
    const [sources, chats] = await Promise.all([
      api.listDataSources(slug),
      api.listConversations(slug, { limit: 1 }),
    ]);
    setTrainedSources(sources.trained_count);
    setConversations(chats.total);
  }, [slug]);

  useEffect(() => {
    void loadProgress();
  }, [loadProgress]);

  if (!slug) return <EmptyState title="No bot selected" />;
  if (state.loading) return <Spinner label="Loading bot…" />;
  if (!state.settings || !state.bot) {
    return <ErrorNotice message={state.error ?? 'This bot has no settings row.'} />;
  }

  const settings = state.settings;

  const checklist: ChecklistItem[] = [
    {
      label: 'Train the assistant',
      done: (trainedSources ?? 0) > 0,
      to: '/data-sources',
      hint: 'Crawl your site so answers come from your own content.',
    },
    {
      label: 'Send a first message',
      done: (conversations ?? 0) > 0,
      to: '/preview',
      hint: 'Use the widget on the right — it is the real thing.',
    },
    {
      label: 'Customise the widget',
      done: Boolean(settings.widget.header_color),
      to: '/settings',
      hint: 'Colours, greeting and suggested messages.',
    },
    {
      label: 'Embed on your site',
      done: state.bot.allowed_origins.length > 0,
      to: '/embed',
      hint: 'Add the script tag and lock the origin allow-list down.',
    },
  ];

  return (
    <div>
      <PageHeader
        title="Preview"
        description={`Quick settings and a live widget for ${botLabel(state.bot)}`}
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              setReloadKey((key) => key + 1);
              void loadProgress();
            }}
          >
            Restart widget
          </Button>
        }
      />

      {state.error && <ErrorNotice message={state.error} />}

      <div className="grid gap-4 lg:grid-cols-[1fr_26rem]">
        <div className="space-y-4">
          <Card className="p-5">
            <h2 className="mb-3 text-base font-semibold">Quick settings</h2>

            <label
              className="block text-sm font-medium text-slate-700"
              htmlFor="quick-prompt"
            >
              Base prompt
            </label>
            <textarea
              id="quick-prompt"
              rows={12}
              value={settings.base_prompt}
              onChange={(event) => state.patch({ base_prompt: event.target.value })}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs outline-none focus:border-brand-blue"
            />
            <p className="mt-1 text-xs text-slate-500">
              {settings.base_prompt.length.toLocaleString()} characters
            </p>

            <div className="mt-4 flex items-center gap-3">
              <Button
                disabled={state.saving}
                onClick={() =>
                  void state
                    .save({
                      base_prompt: settings.base_prompt,
                    })
                    .then(() => setReloadKey((key) => key + 1))
                }
              >
                {state.saving ? 'Saving…' : 'Save and restart widget'}
              </Button>
              <span className="text-xs text-slate-400">
                The widget reads saved settings when it loads.
              </span>
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="mb-3 text-base font-semibold">Quick start</h2>
            <ol className="space-y-2">
              {checklist.map((item) => (
                <li key={item.label} className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs ${
                      item.done
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {item.done ? '✓' : '•'}
                  </span>
                  <span>
                    <Link
                      to={item.to}
                      className="text-sm font-medium text-slate-800 hover:text-brand-blue"
                    >
                      {item.label}
                    </Link>
                    <span className="block text-xs text-slate-500">{item.hint}</span>
                  </span>
                </li>
              ))}
            </ol>
          </Card>
        </div>

        <Card className="p-3">
          <WidgetLivePreview key={reloadKey} slug={slug} />
          <p className="mt-2 px-1 text-[11px] text-slate-400">
            Real widget, real backend. Conversations started here appear in the Chat
            Inbox.
          </p>
        </Card>
      </div>
    </div>
  );
}
